import { Link, useParams } from 'react-router-dom'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FormInput, VerticalForm } from '../../components/Form'
import { forgotPassword } from '../../store/user/userThunk'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Form validation schema
const schemaResolver = yupResolver(
    yup.object().shape({
        password: yup
            .string()
            .required('Please enter Password')
            .min(8, 'Password must be at least 8 characters')
            .max(32, 'Password must be at most 32 characters')
            .matches(/[a-z]/, 'Password must include at least one lowercase letter')
            .matches(/[A-Z]/, 'Password must include at least one uppercase letter')
            .matches(/\d/, 'Password must include at least one number')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must include at least one special character'),
        confirmPassword: yup.string()
            .required('Please confirm password')
            .oneOf([yup.ref('password'), null], 'Passwords must match'),
    })
)

const ResetPassword = () => {
    const dispatch = useDispatch()
    const params = useParams()

    useEffect(() => {
        if (document.body) {
            document.body.classList.add('bg-gradient-to-br', 'from-blue-50', 'to-blue-100', 'tracking-wide')
        }
        return () => {
            if (document.body) {
                document.body.classList.remove('bg-gradient-to-br', 'from-blue-50', 'to-blue-100', 'tracking-wide')
            }
        }
    }, [])

    const onSubmit = async (values, { resetForm }) => {
        try {
            let id = params.id
            let data = {
                id: id,
                password: values?.password
            }
            await dispatch(forgotPassword(data)).unwrap()
            toast.success('Password reset successfully!')
            resetForm()
        } catch (error) {
            toast.error(error || 'Failed to reset password. Please try again.')
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="container mx-auto">
                <div className="flex justify-center">
                    <div className="lg:w-1/2 w-full max-w-md">
                        <div className="bg-white shadow-2xl rounded-2xl p-8 transform transition-all duration-300">
                            <div className="mb-8 text-center">
                                <h6 className="text-2xl font-bold text-gray-800 mt-6">
                                    Reset Your Password
                                </h6>
                                <p className="text-gray-600 text-sm mt-2 mb-6">
                                    Enter your new password and confirm it to reset your account.
                                </p>
                            </div>
                            <VerticalForm
                                onSubmit={onSubmit}
                                resolver={schemaResolver}
                            >
                                <FormInput
                                    label="New Password"
                                    type="password"
                                    name="password"
                                    placeholder="Enter new password"
                                    className="py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-[#50B5FF] focus:ring-2 focus:ring-[#50B5FF]/30 transition duration-200"
                                    containerClass="mb-6"
                                    labelClassName="block text-sm font-medium mb-2 text-gray-700"
                                    labelContainerClassName="flex justify-between items-center mb-0"
                                    required
                                />
                                <FormInput
                                    label="Confirm Password"
                                    type="password"
                                    name="confirmPassword"
                                    placeholder="Confirm new password"
                                    className="py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-[#50B5FF] focus:ring-2 focus:ring-[#50B5FF]/30 transition duration-200"
                                    containerClass="mb-6"
                                    labelClassName="block text-sm font-medium mb-2 text-gray-700"
                                    labelContainerClassName="flex justify-between items-center mb-0"
                                    required
                                />
                                <div className="pt-4 text-center">
                                    <button
                                        className="w-full bg-[#50B5FF] text-white font-semibold py-3 px-4 rounded-lg transition-all hover:bg-[#3a9fee] hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#50B5FF]/50 focus:ring-opacity-50"
                                        type="submit"
                                    >
                                        Reset Password
                                    </button>
                                </div>
                            </VerticalForm>
                            <div className="mt-6 text-center">
                                <p className="text-gray-600 text-sm">
                                    Back to{' '}
                                    <Link
                                        to="/auth/sign-in"
                                        className="text-[#50B5FF] font-semibold hover:underline"
                                    >
                                        Log In
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </div>
    )
}

export default ResetPassword